import { useTranslation } from 'react-i18next';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { observer } from 'mobx-react-lite';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import styles from './OrgSelector.module.scss';
import Organizations from 'src/pages/teams/Organizations';
import { useState } from 'react';
import { STYLE_FOR_LARGE_DIALOG } from 'src/constants/Layout';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/constants/navigateRoutes';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { ROLES } from 'src/constants/Role';

interface OrgSelectorProps {
  className?: string;
  revamp?: boolean;
}

const WORKSPACE_ADMIN_ROLES: Array<keyof typeof ROLES> = [
  ROLES.ADMIN,
  ROLES.DOMAIN_OWNER,
  ROLES.SUPER_ADMIN,
];

const OrgSelector = observer((props: OrgSelectorProps) => {
  const { className, revamp = false } = props;
  const { t } = useTranslation();
  const [dialogueOpen, setDialogueOpen] = useState(false);
  const { organizationDetails } = useOrganizationStore();
  const { getUserDetails } = useAuthStore();
  const navigate = useNavigate();
  const user = getUserDetails();
  const organizationName = organizationDetails?.displayName?.trim() || 'NA';
  const canManageWorkspace = Boolean(
    user && WORKSPACE_ADMIN_ROLES.includes(user.role),
  );

  const openPopupForChangeOrg = () => {
    setDialogueOpen(true);
  };

  const handleDialogClose = () => {
    setDialogueOpen(false);
  };

  const openWorkspaceSettings = (tab: 'organization' | 'members') => {
    handleDialogClose();
    navigate(
      tab === 'members' && !revamp
        ? PATHS.users
        : `${PATHS.settings}?tab=${tab}`,
    );
  };

  return (
    <>
      <ButtonBase
        aria-label={`Change active organization. Current organization ${organizationName}.`}
        className={`${styles.wrapper}${revamp ? ` ${styles.revamp}` : ''}${
          className ? ` ${className}` : ''
        }`}
        focusRipple
        onClick={openPopupForChangeOrg}
      >
        <Box className={styles['content']}>
          <Typography className={styles.label} variant="caption">
            Active Org
          </Typography>
          <Box className={styles.row}>
            <Typography className={styles.ellipsisText} variant="body2">
              {organizationName}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              className={styles.chevron}
              fontSize="small"
            />
          </Box>
          {typeof organizationDetails?.memberCount === 'number' && (
            <Typography variant="caption">
              {t('members_tab_members')}: {organizationDetails.memberCount}
            </Typography>
          )}
        </Box>
      </ButtonBase>
      <Dialog
        PaperProps={{ sx: STYLE_FOR_LARGE_DIALOG }}
        open={dialogueOpen}
        onClose={handleDialogClose}
        className="hanabi-scrollbar"
      >
        <DialogContent>
          {canManageWorkspace && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 2 }}
              aria-label="Workspace controls"
            >
              <Button
                variant="outlined"
                startIcon={<SettingsOutlinedIcon />}
                onClick={() => openWorkspaceSettings('organization')}
                sx={{ justifyContent: 'flex-start' }}
              >
                Workspace settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<GroupOutlinedIcon />}
                onClick={() => openWorkspaceSettings('members')}
                sx={{ justifyContent: 'flex-start' }}
              >
                Members and invites
              </Button>
            </Stack>
          )}
          <Organizations />
        </DialogContent>
      </Dialog>
    </>
  );
});

export default OrgSelector;
